import { useState } from 'react';
import type { DocRecordView } from './api';
import { labels } from './labels';

/**
 * QA-89: **Download JSON** and **Copy JSON** for a finished document. Download saves
 * `{ result, provenance }` as `<type>-<stem>.json`; Copy puts the `result` on the clipboard.
 * It renders a fragment, so the buttons join the action row of the caller.
 */
export function ResultJsonActions({ view }: { view: DocRecordView }) {
  const text = labels.resultJson;
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');

  function download() {
    const json = JSON.stringify({ result: view.raw.result, provenance: view.raw.provenance }, null, 2);
    const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${view.docId.replace('/', '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function copyResult() {
    try {
      await navigator.clipboard.writeText(JSON.stringify(view.raw.result, null, 2));
      setCopy('copied');
    } catch {
      setCopy('failed');
    }
  }

  return (
    <>
      <button type="button" className="btn" onClick={download}>
        {text.download}
      </button>
      <button type="button" className="btn" onClick={() => void copyResult()}>
        {copy === 'copied' ? text.copied : text.copy}
      </button>
      {copy === 'failed' && (
        <span className="action-row__note" role="alert">
          {text.copyFailed}
        </span>
      )}
    </>
  );
}
